import {
  GetQBankStructureByIdDocument,
  useArchiveQBankStructureMutation,
  usePublishQBankStructureMutation,
  useUnpublishQBankStructureMutation,
} from '@exam-management/graphql';
import { DuplicateQBankStructureModal } from '@exam-management/qbank-structure-duplication';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { useNavigate } from '@exam-management/router';
import { Box, ConfirmationModal, Text } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { createContext, useCallback, useMemo, useState, type PropsWithChildren } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  QBANK_STRUCTURE_LEVEL_ITEM_FORM_ACTION_QUERY_KEY,
  QBankStructureLevelItemFormAction,
} from '../../constants';
import { QBankStructureDetailEditModal, ValidateAndPublishModal } from '../../containers';

import type { QBankStructureHeaderModalContextData } from './types';

export enum QBankStructureHeaderModalAction {
  EDIT_INFORMATION = 'EDIT_INFORMATION',
  DUPLICATE = 'DUPLICATE',
  PUBLISH_FROM_DRAFT = 'PUBLISH_FROM_DRAFT',
  PUBLISH_FROM_UNPUBLISHED = 'PUBLISH_FROM_UNPUBLISHED',
  SAVE_AS_PUBLISHED = 'SAVE_AS_PUBLISHED',
  UNPUBLISH = 'UNPUBLISH',
  ARCHIVE = 'ARCHIVE',
}

const QBankStructureHeaderModalContext = createContext<
  QBankStructureHeaderModalContextData | undefined
>(undefined);

type QBankStructureHeaderModalActionProviderProps = PropsWithChildren;

function QBankStructureHeaderModalActionProvider(
  props: Readonly<QBankStructureHeaderModalActionProviderProps>,
) {
  const { children } = props;
  const [action, setAction] = useState<QBankStructureHeaderModalAction | null>(null);
  const { showSnackbar } = useSnackbar();
  const { qBankStructureInformation, formMethodsRef } = useQBankStructure();
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [publishQBankStructure, { loading: isPublishing }] = usePublishQBankStructureMutation({
    onCompleted: () => {
      showSnackbar({ message: 'QBank Structure has been published', variant: 'success' });
    },
    onError: () => {
      showSnackbar({ message: 'Unable to Publish QBank Structure', variant: 'error' });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQBankStructureByIdDocument,
        variables: { id: qBankStructureInformation.id },
        fetchPolicy: 'network-only',
      },
    ],
  });

  const [unpublishQBankStructure, { loading: isUnpublishLoading }] =
    useUnpublishQBankStructureMutation({
      onCompleted: () => {
        showSnackbar({ message: 'QBank Structure has been pending', variant: 'success' });
      },
      onError: () => {
        showSnackbar({ message: 'Unable to Pending QBank Structure', variant: 'error' });
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQBankStructureByIdDocument,
          variables: { id: qBankStructureInformation.id },
          fetchPolicy: 'network-only',
        },
      ],
    });

  const [archiveQBankStructure, { loading: isArchiving }] = useArchiveQBankStructureMutation({
    onCompleted: () => {
      showSnackbar({ message: 'QBank Structure has been disabled', variant: 'success' });
    },
    onError: () => {
      showSnackbar({ message: 'Unable to Disable QBank Structure', variant: 'error' });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQBankStructureByIdDocument,
        variables: { id: qBankStructureInformation.id },
        fetchPolicy: 'network-only',
      },
    ],
  });

  const handleSetToView = () => {
    setSearchParams({
      [QBANK_STRUCTURE_LEVEL_ITEM_FORM_ACTION_QUERY_KEY]: QBankStructureLevelItemFormAction.VIEW,
    });
  };

  const handlePublishFromDraft = async () => {
    await publishQBankStructure({
      variables: { id: qBankStructureInformation.id },
    });
    handleClose();
  };

  const handlePublishFromUnPublished = useCallback(async () => {
    const formContext = formMethodsRef.current?.formContext;
    const onSubmit = formMethodsRef.current?.onSubmit;

    if (formContext && onSubmit) {
      setIsSubmitting(true);
      await formContext.handleSubmit(async (data) => {
        try {
          await onSubmit(data);
        } catch (error) {
          console.error(
            'Error during form submission from save as unpublished while publishing:',
            error,
          );
          return;
        }

        await publishQBankStructure({
          variables: { id: qBankStructureInformation.id },
        });
        handleSetToView();
      })();

      setIsSubmitting(false);
      handleClose();
      return;
    }

    await publishQBankStructure({
      variables: { id: qBankStructureInformation.id },
    });
    handleSetToView();
    handleClose();
  }, [formMethodsRef, publishQBankStructure, qBankStructureInformation.id]);

  const handleUnpublishStructure = async () => {
    await unpublishQBankStructure({
      variables: { unpublishQBankStructureId: qBankStructureInformation.id },
    });
    handleClose();
  };

  const handleSubmitArchiveStructure = useCallback(async () => {
    const formContext = formMethodsRef.current?.formContext;
    const onSubmit = formMethodsRef.current?.onSubmit;

    if (formContext && onSubmit) {
      setIsSubmitting(true);
      await formContext.handleSubmit(async (data) => {
        try {
          await onSubmit(data);
        } catch (error) {
          console.error(
            'Error during form submission from save as unpublished while archiving:',
            error,
          );
          return;
        }

        await archiveQBankStructure({
          variables: { archiveQBankStructureId: qBankStructureInformation.id },
        });
        handleSetToView();
      })();

      setIsSubmitting(false);
      handleClose();
      return;
    }

    await archiveQBankStructure({
      variables: { archiveQBankStructureId: qBankStructureInformation.id },
    });
    handleSetToView();
    handleClose();
  }, [archiveQBankStructure, formMethodsRef, qBankStructureInformation.id]);

  const handleSetAction = (action: QBankStructureHeaderModalAction | null) => {
    setAction(action);
  };

  const handleClose = () => {
    setAction(null);
  };

  const contextValue = useMemo(() => {
    return {
      setAction: handleSetAction,
    };
  }, []);

  return (
    <QBankStructureHeaderModalContext.Provider value={contextValue}>
      {children}
      <QBankStructureDetailEditModal
        status={qBankStructureInformation.status}
        open={action === QBankStructureHeaderModalAction.EDIT_INFORMATION}
        onClose={handleClose}
      />
      <DuplicateQBankStructureModal
        qBankStructureId={qBankStructureInformation.id}
        name={qBankStructureInformation.name}
        open={action === QBankStructureHeaderModalAction.DUPLICATE}
        onCompleted={(res) => {
          navigate('/qbank-structures/:structureId', {
            params: { structureId: res.qbsDuplicateQBankStructure.id },
          });
          handleClose();
        }}
        onClose={handleClose}
      />
      <ValidateAndPublishModal
        open={action === QBankStructureHeaderModalAction.PUBLISH_FROM_DRAFT}
        onClose={handleClose}
        onPublish={handlePublishFromDraft}
        loading={isPublishing}
      />
      {/*TODO: Fix padding in ConfirmationModal*/}
      <ConfirmationModal
        title='Pending QBank Structure?'
        acceptText='Pending'
        open={action === QBankStructureHeaderModalAction.UNPUBLISH}
        onClose={handleClose}
        onDiscard={handleClose}
        onAccept={handleUnpublishStructure}
        loading={isUnpublishLoading}
        content={
          <Box>
            <Text>เมื่อทำการ Pending แล้ว</Text>
            <Text variant='body1Semibold'>{`ข้อมูลจะไม่สามารถนำไปสร้าง QConfig ได้\n `}</Text>
            <Text>กรุณาตรวจสอบความถูกต้องให้เรียบร้อยก่อนดำเนินการต่อ</Text>
          </Box>
        }
      />
      <ConfirmationModal
        title='Disable QBank Structure?'
        acceptText='Disable'
        open={action === QBankStructureHeaderModalAction.ARCHIVE}
        onClose={handleClose}
        onDiscard={handleClose}
        onAccept={handleSubmitArchiveStructure}
        loading={isArchiving || isSubmitting}
        content={
          <Box>
            <Text>เมื่อทำการ Disable แล้ว</Text>
            <Text variant='body1Semibold'>{`จะไม่สามารถเพิ่มคำถามใน Question List และ \nไม่สามารถนำข้อมูลไปสร้าง QConfig ได้ \n `}</Text>
            <Text>กรุณาตรวจสอบความถูกต้องให้เรียบร้อยก่อนดำเนินการต่อ</Text>
          </Box>
        }
      />
      <ConfirmationModal
        title='Publish QBank Structure?'
        acceptText='Publish'
        open={action === QBankStructureHeaderModalAction.PUBLISH_FROM_UNPUBLISHED}
        onClose={handleClose}
        onDiscard={handleClose}
        onAccept={handlePublishFromUnPublished}
        loading={isPublishing || isSubmitting}
        content={
          <Box>
            <Text>เมื่อทำการ Publish แล้ว</Text>
            <Text variant='body1Semibold'>{`จะไม่สามารถแก้ไขการตั้งค่าระดับโครงสร้างได้อีก \n `}</Text>
            <Text>กรุณาตรวจสอบความถูกต้องให้เรียบร้อยก่อนดำเนินการต่อ</Text>
          </Box>
        }
      />
    </QBankStructureHeaderModalContext.Provider>
  );
}
export { QBankStructureHeaderModalActionProvider, QBankStructureHeaderModalContext };
